<template>
  <div class="content bg8">
    <div class="platform-shell">
      <section class="platform-hero">
        <div class="platform-hero-copy">
          <div class="eyebrow">Platform + Advisory</div>
          <h1>A practical operating system for founders who need focus, clarity, and follow-through.</h1>
          <p class="hero-lead">
            Foclarity helps founders, solopreneurs, and small teams turn scattered priorities into a clear board,
            aligned ownership, visible progress, and AI-supported execution.
          </p>
          <p class="hero-sub">
            This is not “AI for the sake of AI.” It is a structured way to decide what matters most, keep the right work moving,
            and make progress easier to see and manage.
          </p>
          <div class="hero-actions">
            <a href="https://calendly.com/foclarity" target="_blank" rel="nofollow" class="hero-link">
              <button class="hero-btn primary">Schedule a Demo</button>
            </a>
            <a href="/contact" class="hero-link">
              <button class="hero-btn secondary">Talk It Through</button>
            </a>
          </div>
        </div>
        <div class="platform-hero-art">
          <div class="hero-image-card">
            <img src="@/assets/solution-image.png" alt="Foclarity Solution" class="hero-image" />
          </div>
        </div>
      </section>

      <section class="signal-grid">
        <div class="signal-card">
          <strong>Custom boards</strong>
          <span>Build scorecards around your real priorities, not generic templates.</span>
        </div>
        <div class="signal-card">
          <strong>Clear ownership</strong>
          <span>Assign people, work areas, and accountability in one place.</span>
        </div>
        <div class="signal-card">
          <strong>Visible progress</strong>
          <span>Track updates, patterns, and movement over time.</span>
        </div>
        <div class="signal-card">
          <strong>AI support</strong>
          <span>Generate tasks, summaries, and next steps from real business context.</span>
        </div>
      </section>

      <section class="content-section two-col">
        <div class="section-copy">
          <div class="eyebrow">The Problem</div>
          <h2>When everything feels urgent, the real priorities get lost.</h2>
          <p>
            Most early founders and small business owners are carrying too much at once. They are making decisions on the fly,
            juggling competing demands, and trying to keep momentum without a shared system for what matters most.
          </p>
          <p>
            That usually shows up as reactive work, vague ownership, stalled initiatives, and a team that stays busy without being fully aligned.
          </p>
        </div>
        <div class="pain-card">
          <h3>What that often looks like</h3>
          <ul>
            <li>Too many priorities competing for attention</li>
            <li>No shared view of what matters most</li>
            <li>Unclear ownership across key work areas</li>
            <li>Progress that is hard to measure or explain</li>
            <li>Tasks happening without a strong connection to strategy</li>
          </ul>
        </div>
      </section>

      <section class="content-section">
        <div class="section-copy narrow">
          <div class="eyebrow">What The Platform Does</div>
          <h2>Foclarity turns priorities into a working system.</h2>
          <p>
            The platform is designed to help you structure what matters, connect it to real work, and maintain alignment over time.
            It gives you a way to move from “we should do this” to “this is owned, tracked, and moving.”
          </p>
        </div>
        <div class="feature-grid">
          <div class="feature-card">
            <h3>Start with a template or build your own</h3>
            <p>Use an existing board as a starting point, then clone and customize it around your business.</p>
          </div>
          <div class="feature-card">
            <h3>Organize the work clearly</h3>
            <p>Break priorities into categories, measures, and outcomes your team can understand and act on.</p>
          </div>
          <div class="feature-card">
            <h3>Connect strategy to execution</h3>
            <p>Create tasks tied directly to the board so the work stays connected to the priorities.</p>
          </div>
          <div class="feature-card">
            <h3>Track status and momentum</h3>
            <p>Capture updates over time, see what is behind or on track, and make progress visible.</p>
          </div>
          <div class="feature-card">
            <h3>Align the people involved</h3>
            <p>Invite collaborators, assign responsibility, and keep everyone working from the same picture.</p>
          </div>
          <div class="feature-card">
            <h3>Use AI where it adds leverage</h3>
            <p>Generate executive summaries, next steps, and task support from the actual board data and updates.</p>
          </div>
        </div>
      </section>

      <section class="content-section two-col">
        <div class="best-fit-card">
          <div class="eyebrow">Best For</div>
          <h2>Who gets the most value from Foclarity</h2>
          <ul>
            <li>Early founders trying to grow with more intention</li>
            <li>Solopreneurs who need clearer priorities and better structure</li>
            <li>Owner-operators juggling too many initiatives at once</li>
            <li>Small teams that need better alignment around what matters most</li>
            <li>Leaders who want more traction without adding more chaos</li>
          </ul>
        </div>
        <div class="best-fit-card accent">
          <div class="eyebrow">Why It Lands</div>
          <h2>This is especially useful when:</h2>
          <ul>
            <li>You know you are capable of more progress than you are currently making</li>
            <li>Your team is working hard but not always in the same direction</li>
            <li>You need to turn goals into a system people actually use</li>
            <li>You want AI to support execution, not replace good judgment</li>
          </ul>
        </div>
      </section>

      <section class="content-section">
        <div class="section-copy narrow">
          <div class="eyebrow">What You Get Working With Me</div>
          <h2>Foclarity is stronger because it is not just software.</h2>
          <p>
            The platform is useful on its own, but the real lift comes from using it with a founder-focused process.
            I help you clarify what matters, shape the board around your actual business, and keep the system grounded in reality.
          </p>
        </div>
        <div class="service-grid">
          <div class="service-card">
            <h3>Strategic clarity</h3>
            <p>We identify the priorities, outcomes, and decisions that matter most right now.</p>
          </div>
          <div class="service-card">
            <h3>Custom scorecard design</h3>
            <p>We build a board around your real goals, not somebody else’s framework.</p>
          </div>
          <div class="service-card">
            <h3>Team alignment</h3>
            <p>We make ownership visible so the right people are accountable for the right work.</p>
          </div>
          <div class="service-card">
            <h3>Execution support</h3>
            <p>We use the platform and AI tools to turn priorities into next steps, tasks, and steady momentum.</p>
          </div>
        </div>
      </section>

      <section class="content-section compare-section">
        <div class="section-copy narrow">
          <div class="eyebrow">Why This Is Different</div>
          <h2>More useful than a generic project tool. More grounded than AI alone.</h2>
        </div>
        <div class="compare-grid">
          <div class="compare-card muted">
            <h3>Project tools alone</h3>
            <p>Good for managing tasks after priorities are already clear.</p>
            <ul>
              <li>Often disconnected from strategy</li>
              <li>Easy to become another busywork system</li>
              <li>Does not solve focus by itself</li>
            </ul>
          </div>
          <div class="compare-card featured">
            <h3>Foclarity</h3>
            <p>Built to help founders decide what matters, align the work, and keep momentum visible.</p>
            <ul>
              <li>Custom boards built around actual priorities</li>
              <li>Tasks tied directly to focus areas and outcomes</li>
              <li>Progress tracking, summaries, and AI support in one rhythm</li>
            </ul>
          </div>
          <div class="compare-card muted">
            <h3>ChatGPT alone</h3>
            <p>Useful for ideas and drafting, but it does not hold the team’s operating context.</p>
            <ul>
              <li>No built-in ownership or status tracking</li>
              <li>No shared board of record</li>
              <li>No ongoing structure for alignment</li>
            </ul>
          </div>
        </div>
      </section>

      <section class="testimonial-band">
        <div class="testimonial-card">
          <div class="eyebrow">Why Teams Love Foclarity</div>
          <blockquote>
            “Before Foclarity, we were swimming in an endless sea of great ideas and possibilities, but we had no anchor to tether them to.
            Foclarity worked with us to clarify our offerings, focus on strategic steps forward and ensure that our vision was aligned.
            The platform and people behind it have played a pivotal role in Vault's evolution as a startup.”
          </blockquote>
          <p>Allison Beck, Co-Founder, Vault</p>
        </div>
      </section>

      <section class="cta-band">
        <div class="cta-copy">
          <div class="eyebrow">Next Step</div>
          <h2>Ready to get everyone focused on what matters most?</h2>
          <p>
            If you want more clarity, alignment, and traction in your business, let’s talk about how Foclarity can support the way you actually work.
          </p>
        </div>
        <div class="hero-actions">
          <a href="https://calendly.com/foclarity" target="_blank" rel="nofollow" class="hero-link">
            <button class="hero-btn primary">Book a Demo</button>
          </a>
          <a href="/contact" class="hero-link">
            <button class="hero-btn secondary">Start the Conversation</button>
          </a>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  title: 'Founder Operating System for Focus, Clarity and Execution | Foclarity',
  description: 'Use Foclarity to turn scattered priorities into clear scorecards, ownership, visible progress, and AI-supported execution for your team.',
  name: "Platform",
};
</script>

<style lang="scss" scoped>
.platform-shell {
  max-width: 1180px;
  margin: 0 auto;
  padding: 48px 24px 72px;
}

.eyebrow {
  color: #9a6b00;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 14px;
}

.platform-hero {
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 40px;
  align-items: center;
  padding: 28px 0 42px;
}

.platform-hero-copy h1 {
  font-size: clamp(2.4rem, 4vw, 4.2rem);
  line-height: 1.05;
  color: #10263d;
  margin: 0 0 18px;
}

.hero-lead {
  font-size: 1.2rem;
  line-height: 1.6;
  color: #29435c;
  margin-bottom: 18px;
}

.hero-sub {
  font-size: 1rem;
  line-height: 1.7;
  color: #4f6478;
  max-width: 720px;
}

.platform-hero-art {
  display: flex;
  justify-content: center;
}

.hero-image-card {
  width: 100%;
  max-width: 420px;
  background: linear-gradient(180deg, #ffffff, #eef4f8);
  border: 1px solid #d6e1ea;
  border-radius: 28px;
  padding: 18px;
  box-shadow: 0 30px 70px rgba(16, 38, 61, 0.14);
}

.hero-image {
  display: block;
  width: 100%;
  border-radius: 18px;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 28px;
}

.hero-link {
  text-decoration: none;
}

.hero-btn {
  border: none;
  border-radius: 999px;
  padding: 14px 24px;
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.hero-btn:hover {
  transform: translateY(-1px);
}

.hero-btn.primary {
  background: linear-gradient(135deg, #12395b, #246794);
  color: #fff;
  box-shadow: 0 14px 30px rgba(18, 57, 91, 0.24);
}

.hero-btn.secondary {
  background: #fff;
  color: #12395b;
  border: 1px solid #c8d6e2;
}

.signal-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin: 0 0 32px;
}

.signal-card {
  background: #fff;
  border: 1px solid #d8e2eb;
  border-radius: 20px;
  padding: 18px;
  box-shadow: 0 10px 24px rgba(13, 34, 56, 0.05);
}

.signal-card strong {
  display: block;
  color: #12395b;
  margin-bottom: 8px;
  font-size: 1rem;
}

.signal-card span {
  display: block;
  color: #5f7285;
  line-height: 1.55;
  font-size: 0.95rem;
}

.content-section {
  margin: 26px 0;
  background: #fff;
  border: 1px solid #dde5ec;
  border-radius: 28px;
  padding: 34px;
  box-shadow: 0 16px 36px rgba(13, 34, 56, 0.05);
}

.two-col {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 28px;
  align-items: start;
}

.section-copy h2,
.best-fit-card h2,
.cta-copy h2 {
  color: #12304b;
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  line-height: 1.15;
  margin: 0 0 16px;
}

.section-copy p,
.best-fit-card p,
.cta-copy p,
.feature-card p,
.service-card p,
.compare-card p,
.pain-card p {
  color: #52677b;
  line-height: 1.7;
  font-size: 1rem;
}

.narrow {
  max-width: 760px;
}

.pain-card,
.best-fit-card,
.feature-card,
.service-card,
.compare-card,
.testimonial-card {
  border-radius: 22px;
}

.pain-card,
.best-fit-card {
  background: linear-gradient(180deg, #f7fafc, #f0f5f9);
  border: 1px solid #d8e2ea;
  padding: 24px;
}

.best-fit-card.accent {
  background: linear-gradient(180deg, #12395b, #184d78);
  border-color: #184d78;
}

.best-fit-card.accent,
.best-fit-card.accent h2,
.best-fit-card.accent li,
.best-fit-card.accent .eyebrow {
  color: #fff;
}

.pain-card h3 {
  color: #12395b;
  margin: 0 0 12px;
}

.pain-card ul,
.best-fit-card ul,
.compare-card ul {
  margin: 0;
  padding-left: 20px;
}

.pain-card li,
.best-fit-card li,
.compare-card li {
  color: #52677b;
  line-height: 1.7;
  margin-bottom: 8px;
}

.feature-grid,
.service-grid,
.compare-grid {
  display: grid;
  gap: 18px;
  margin-top: 26px;
}

.feature-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.service-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.compare-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.feature-card,
.service-card,
.compare-card {
  padding: 22px;
  border: 1px solid #dbe4eb;
}

.feature-card {
  background: linear-gradient(180deg, #ffffff, #f7fafc);
}

.service-card {
  background: linear-gradient(180deg, #f8fbfd, #edf4f8);
}

.compare-card.muted {
  background: #f8fbfd;
}

.compare-card.featured {
  background: linear-gradient(180deg, #12395b, #184d78);
  border-color: #184d78;
  box-shadow: 0 20px 40px rgba(18, 57, 91, 0.18);
}

.compare-card.featured h3,
.compare-card.featured p,
.compare-card.featured li {
  color: #fff;
}

.feature-card h3,
.service-card h3,
.compare-card h3 {
  color: #12395b;
  margin: 0 0 10px;
  font-size: 1.08rem;
  line-height: 1.35;
}

.testimonial-band,
.cta-band {
  margin-top: 26px;
}

.testimonial-card {
  background: linear-gradient(135deg, #f3f0ff, #eef7fb);
  border: 1px solid #d7dfeb;
  padding: 34px;
}

.testimonial-card blockquote {
  margin: 0 0 18px;
  color: #24435d;
  font-size: 1.15rem;
  line-height: 1.7;
}

.testimonial-card p {
  margin: 0;
  color: #587086;
  font-weight: 600;
}

.cta-band {
  background: linear-gradient(135deg, #fff7e6, #fff1d2);
  border: 1px solid #ecd8a4;
  border-radius: 28px;
  padding: 34px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  align-items: center;
}

@media (max-width: 980px) {
  .platform-hero,
  .two-col,
  .cta-band {
    grid-template-columns: 1fr;
  }

  .signal-grid,
  .feature-grid,
  .service-grid,
  .compare-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 680px) {
  .platform-shell {
    padding: 28px 16px 56px;
  }

  .content-section,
  .cta-band,
  .testimonial-card {
    padding: 24px 20px;
  }

  .signal-grid,
  .feature-grid,
  .service-grid,
  .compare-grid {
    grid-template-columns: 1fr;
  }

  .hero-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .hero-btn {
    width: 100%;
  }
}
</style>
